import type { CanvasAabbOfRotatedPathParams } from '../types/canvas-aabb-of-rotated-path-params'

/** Degrees in [0, 360); treats near-0 / near-360 as axis-aligned for bbox policy. */
export function canonicalRotationDegreesForAabb(rotationDeg: number | undefined): number {
  const d = Number.isFinite(rotationDeg) ? (rotationDeg as number) : 0

  return ((d % 360) + 360) % 360
}

/**
 * Axis-aligned canvas bounds of the path's local bbox after the same rotation as `ShapeRenderer`:
 * `rotate(deg)` with default origin at the center of the shape placement frame (width × height at shapeX, shapeY).
 * Uses the same (cos, sin) convention as multi-select rotation (`computeMultiRotatePreview` in
 * `lib/transforms/multi-rotation.ts`).
 *
 * When rotation is ~0°, uses the **placement frame** (0,0,shapeWidth,shapeHeight) as local bounds instead of
 * `getBBox()`-style path bounds: SVG stroke extends outside the fill and inflates unions (e.g. two W:1 squares
 * reading as 2.0019 instead of 2).
 */
export function canvasAabbOfRotatedPathBounds(params: CanvasAabbOfRotatedPathParams): {
  minX: number
  minY: number
  maxX: number
  maxY: number
} {
  const {
    shapeX: sx,
    shapeY: sy,
    shapeWidth: sw,
    shapeHeight: sh,
    boundsX: bxIn,
    boundsY: byIn,
    boundsWidth: bwIn,
    boundsHeight: bhIn,
    rotationDeg: rotationDegRaw,
  } = params

  const rot = Number.isFinite(rotationDegRaw) ? (rotationDegRaw as number) : 0
  const rNorm = canonicalRotationDegreesForAabb(rot)
  const axisAligned = rNorm <= 1e-4 || rNorm >= 360 - 1e-4

  let bx = bxIn ?? 0
  let by = byIn ?? 0
  let bw = sw === 0 ? 0 : (bwIn ?? sw)
  let bh = sh === 0 ? 0 : (bhIn ?? sh)

  if (axisAligned) {
    bx = 0
    by = 0
    bw = sw === 0 ? 0 : sw
    bh = sh === 0 ? 0 : sh
  }

  const ox = sx + sw / 2
  const oy = sy + sh / 2
  const rad = (rot * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  const corners = [
    { x: sx + bx, y: sy + by },
    { x: sx + bx + bw, y: sy + by },
    { x: sx + bx + bw, y: sy + by + bh },
    { x: sx + bx, y: sy + by + bh },
  ]

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const c of corners) {
    const dx = c.x - ox
    const dy = c.y - oy
    const rx = ox + dx * cos - dy * sin
    const ry = oy + dx * sin + dy * cos

    minX = Math.min(minX, rx)
    minY = Math.min(minY, ry)
    maxX = Math.max(maxX, rx)
    maxY = Math.max(maxY, ry)
  }

  return { minX, minY, maxX, maxY }
}
